import { useEffect, useState } from 'react';
import {
  IMembershipType,
  useQueryGetUserMembership,
} from '@ifit/gateway-wolf-athenaeum-service';
import { waitForData } from '@ifit/native-webview-interface';
import { UserInfo } from '@ifit/native-webview-interface/out/good-cop-keys';
import { useConfig } from '../../../store/reducers/config';
import {
  IV1GetUserResponseOK,
  useQueryV1GetUser,
} from '@ifit/gateway-cockatoo';

export type Membership = {
  membershipType: IMembershipType;
  isPaused: boolean;
  isFreeLibrary: boolean | undefined;
};

export const useUserQuery = (): IV1GetUserResponseOK => {
  const { data: userData } = useQueryV1GetUser(
    { useGatewayCache: true },
    { staleTime: 120000 },
  );
  return userData;
};

export const useMembership = (): Membership => {
  const {
    membershipType: configuredMembershipType,
    isPaused: configuredIsPaused,
    isEmbedded,
  } = useConfig();

  const [membershipType, setMembershipType] = useState<IMembershipType>(
    configuredMembershipType,
  );

  const userData = useUserQuery();

  const [isPaused, setIsPaused] = useState<boolean | undefined>(
    configuredMembershipType !== undefined
      ? configuredIsPaused ?? false
      : undefined,
  );
  const [localDataLoading, setLocalDataLoading] = useState(
    configuredMembershipType === undefined,
  );

  const { data } = useQueryGetUserMembership(null, {
    enabled:
      !localDataLoading &&
      membershipType === undefined &&
      isPaused === undefined,
  });

  useEffect(() => {
    if (userData && membershipType) {
      if (
        membershipType === IMembershipType.Premium &&
        isEmbedded &&
        !userData.has_glass_access
      ) {
        setMembershipType(IMembershipType.Free);
      }
    } else if (data) {
      if (isEmbedded && data.isPremium && !userData?.has_glass_access) {
        setMembershipType(IMembershipType.Free);
      } else {
        setMembershipType(
          data.isPremium ? IMembershipType.Premium : IMembershipType.Free,
        );
      }
      setIsPaused(data.isPaused);
    } else if (localDataLoading) {
      (async () => {
        const localData = await waitForData([
          UserInfo.MembershipType,
          UserInfo.IsPaused,
        ]);
        if (localData) {
          if (localData[UserInfo.MembershipType] === IMembershipType.Premium) {
            setMembershipType(
              isEmbedded && !userData?.has_glass_access
                ? IMembershipType.Free
                : IMembershipType.Premium,
            );
          } else {
            setMembershipType(localData[UserInfo.MembershipType]);
          }
          setIsPaused(localData[UserInfo.IsPaused]);
        }
        setLocalDataLoading(false);
      })();
    }
  }, [localDataLoading, userData, isEmbedded, data, membershipType]);

  return {
    membershipType,
    isPaused,
    isFreeLibrary: membershipType
      ? membershipType === IMembershipType.Free
      : undefined,
  };
};
